import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { api } from '../api';

const DynamicTimeSlotPicker = ({ 
  amenityType, 
  selectedDate, 
  onSlotSelect, 
  selectedStartTime, 
  selectedEndTime 
}) => {
  const { user } = useContext(AuthContext);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [amenityConfig, setAmenityConfig] = useState(null);

  useEffect(() => {
    if (amenityType && selectedDate) {
      loadAvailableSlots();
    }
  }, [amenityType, selectedDate]);

  const loadAvailableSlots = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/amenities/availability', {
        params: {
          amenity_type: amenityType,
          date: selectedDate,
          hoa_id: user.hoa_id
        }
      });

      if (response.data.success) {
        setAvailableSlots(response.data.slots || []);
        setAmenityConfig(response.data.amenity_config || {});
      }
    } catch (error) {
      console.error('Failed to load available slots:', error);
      // Fallback to basic time slots if API fails
      generateFallbackSlots();
    } finally {
      setLoading(false);
    }
  };

  const generateFallbackSlots = () => {
    const slots = [];
    const startHour = 8;
    const endHour = 22;
    const blockMinutes = 60; // Default to 1-hour blocks
    
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += blockMinutes) {
        const startTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const endMinute = minute + blockMinutes;
        const endTimeHour = hour + Math.floor(endMinute / 60);
        const endTimeMinute = endMinute % 60;
        const endTime = `${endTimeHour.toString().padStart(2, '0')}:${endTimeMinute.toString().padStart(2, '0')}`;
        
        if (endTimeHour <= endHour) {
          slots.push({
            start_time: startTime,
            end_time: endTime,
            available: true,
            available_capacity: 1,
            total_capacity: 1
          });
        }
      }
    }
    setAvailableSlots(slots);
  };

  const handleSlotClick = (slot) => {
    if (!slot.available) return;
    
    onSlotSelect({
      start_time: slot.start_time,
      end_time: slot.end_time,
      duration_minutes: amenityConfig?.block_minutes || 60
    });
  };

  const isSlotSelected = (slot) => {
    return selectedStartTime === slot.start_time && selectedEndTime === slot.end_time;
  };

  const getSlotClassName = (slot) => {
    let baseClass = "p-3 m-1 rounded-lg border text-center cursor-pointer transition-all text-sm font-medium ";
    
    if (!slot.available) {
      return baseClass + "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200";
    }
    
    if (isSlotSelected(slot)) {
      return baseClass + "bg-blue-600 text-white border-blue-600 ring-2 ring-blue-300";
    }
    
    if (slot.available_capacity < slot.total_capacity) {
      return baseClass + "bg-yellow-50 text-yellow-800 border-yellow-200 hover:bg-yellow-100";
    }
    
    return baseClass + "bg-green-50 text-green-800 border-green-200 hover:bg-green-100";
  };

  const formatTimeSlot = (slot) => {
    return `${slot.start_time} - ${slot.end_time}`;
  };

  const getDurationBadge = () => {
    if (!amenityConfig?.block_minutes) return null;
    
    const minutes = amenityConfig.block_minutes;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    let duration = '';
    if (hours > 0) duration += `${hours}h`;
    if (mins > 0) duration += `${mins}m`;
    
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        {duration} slots
      </span>
    );
  };

  const getAdvanceBookingBadge = () => {
    const advanceDays = amenityConfig?.advance_booking_days || 14;
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        📅 {advanceDays} days advance booking
      </span>
    );
  };

  if (!amenityType || !selectedDate) {
    return (
      <div className="text-center py-4 text-gray-500">
        Please select an amenity type and date first
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
        <p className="text-gray-600">Loading available time slots...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-md font-medium text-gray-900">Available Time Slots</h4>
        <div className="flex gap-2">
          {getDurationBadge()}
          {getAdvanceBookingBadge()}
        </div>
      </div>
      
      <div className="text-sm text-gray-600 mb-4">
        Select a time slot for <strong>{amenityType}</strong> on{' '}
        <strong>{new Date(selectedDate).toLocaleDateString()}</strong>
        {amenityConfig?.description && (
          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md text-blue-800">
            <strong>About this amenity:</strong> {amenityConfig.description}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {availableSlots.map((slot, index) => (
          <div
            key={index}
            onClick={() => handleSlotClick(slot)}
            className={getSlotClassName(slot)}
            title={
              slot.available 
                ? `Available (${slot.available_capacity}/${slot.total_capacity})`
                : 'Not available'
            }
          >
            <div className="font-medium">{formatTimeSlot(slot)}</div>
            <div className="text-xs mt-1">
              {slot.available ? (
                slot.available_capacity === slot.total_capacity ? (
                  <span className="text-green-600">✓ Available</span>
                ) : (
                  <span className="text-yellow-600">⚠ Limited</span>
                )
              ) : (
                <span className="text-gray-400">✗ Full</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {availableSlots.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <div className="text-4xl mb-2">🚫</div>
          <p>No time slots available for this date</p>
          <p className="text-sm mt-1">Please try selecting a different date</p>
        </div>
      )}

      {selectedStartTime && selectedEndTime && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-blue-800 font-medium">Selected:</span>
            <span className="text-blue-900">
              {selectedStartTime} - {selectedEndTime}
            </span>
            <span className="text-xs text-blue-600">
              ({amenityConfig?.block_minutes || 60} minutes)
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicTimeSlotPicker;